import type {
  AnamnesisTechnicalEventName,
  AnamnesisTelemetryPayload,
} from '@/features/encounters/anamnesis/anamnesisTechnicalTelemetry'
import {
  sanitizeAnamnesisTelemetryPayload,
  getAnamnesisTelemetryEnvironment,
} from '@/features/encounters/anamnesis/anamnesisTechnicalTelemetry'
import {
  ANAMNESIS_SENTRY_TRANSACTIONS,
  bindAnamnesisSentryConsultation,
  captureAnamnesisSentryMessage,
  unbindAnamnesisSentryConsultation,
  withAnamnesisSentrySpan,
} from '@/shared/monitoring/sentry.client'
import { mirrorChunkLifecycleToObservability } from '@/shared/monitoring/chunk-monitoring'
import { Sentry } from '@/shared/lib/sentry'
import { runSentrySafely } from '@/shared/monitoring/sentry.helpers'
import { sanitizeSentryAnamnesisContext } from '@/shared/monitoring/telemetry-sanitizer'

export {
  bindAnamnesisSentryConsultation,
  unbindAnamnesisSentryConsultation,
  getSentryTracePropagationHeaders,
} from '@/shared/monitoring/sentry.client'

export { buildDistributedTracingHeaders } from '@/shared/observability/trace-context'

const EVENT_TO_SENTRY_TRANSACTION: Partial<
  Record<AnamnesisTechnicalEventName, (typeof ANAMNESIS_SENTRY_TRANSACTIONS)[keyof typeof ANAMNESIS_SENTRY_TRANSACTIONS]>
> = {
  microphone_start: ANAMNESIS_SENTRY_TRANSACTIONS.START_RECORDING,
  microphone_stop: ANAMNESIS_SENTRY_TRANSACTIONS.START_RECORDING,
  microphone_permission_denied: ANAMNESIS_SENTRY_TRANSACTIONS.START_RECORDING,
  microphone_error: ANAMNESIS_SENTRY_TRANSACTIONS.START_RECORDING,
  transcription_chunk_confirmed: ANAMNESIS_SENTRY_TRANSACTIONS.CHUNK_PROCESSING,
  streaming_restart: ANAMNESIS_SENTRY_TRANSACTIONS.CHUNK_PROCESSING,
  streaming_max_session: ANAMNESIS_SENTRY_TRANSACTIONS.CHUNK_PROCESSING,
  transcription_session_degraded: ANAMNESIS_SENTRY_TRANSACTIONS.CHUNK_PROCESSING,
  external_whisper_upstream: ANAMNESIS_SENTRY_TRANSACTIONS.TRANSCRIPTION_PIPELINE,
  transcription_fallback_used: ANAMNESIS_SENTRY_TRANSACTIONS.TRANSCRIPTION_PIPELINE,
  transcription_engine_switched: ANAMNESIS_SENTRY_TRANSACTIONS.TRANSCRIPTION_PIPELINE,
  transcription_provider_failed: ANAMNESIS_SENTRY_TRANSACTIONS.TRANSCRIPTION_PIPELINE,
  transcription_engine_selected: ANAMNESIS_SENTRY_TRANSACTIONS.TRANSCRIPTION_PIPELINE,
  diarization_failed: ANAMNESIS_SENTRY_TRANSACTIONS.DIARIZATION_PIPELINE,
  speaker_classified: ANAMNESIS_SENTRY_TRANSACTIONS.DIARIZATION_PIPELINE,
  speaker_low_confidence: ANAMNESIS_SENTRY_TRANSACTIONS.DIARIZATION_PIPELINE,
  speaker_verification_failed: ANAMNESIS_SENTRY_TRANSACTIONS.DIARIZATION_PIPELINE,
  ai_insight_requested: ANAMNESIS_SENTRY_TRANSACTIONS.AI_ANALYSIS_PIPELINE,
  ai_insight_completed: ANAMNESIS_SENTRY_TRANSACTIONS.INSIGHT_RENDER,
  ai_insight_failed: ANAMNESIS_SENTRY_TRANSACTIONS.AI_ANALYSIS_PIPELINE,
  final_summary_requested: ANAMNESIS_SENTRY_TRANSACTIONS.FINAL_SUMMARY_PIPELINE,
  final_summary_completed: ANAMNESIS_SENTRY_TRANSACTIONS.FINAL_SUMMARY_PIPELINE,
  final_summary_failed: ANAMNESIS_SENTRY_TRANSACTIONS.FINAL_SUMMARY_PIPELINE,
}

/** Telemetria operacional esperada — span/breadcrumb sim; sem issue Sentry (SYNAP-DOC-2B). */
const EXPECTED_OPERATIONAL_EVENTS = new Set<AnamnesisTechnicalEventName>(['transcription_fallback_used'])

const STREAMING_WARNING_EVENTS = new Set<AnamnesisTechnicalEventName>([
  'streaming_restart',
  'streaming_max_session',
  'transcription_session_degraded',
  'transcription_provider_failed',
])

export const mirrorAnamnesisTechnicalEventToSentry = (
  name: AnamnesisTechnicalEventName,
  payload: AnamnesisTelemetryPayload = {},
): void => {
  runSentrySafely(() => {
    const consultationId =
      (typeof payload.consultationId === 'string' && payload.consultationId) || undefined
    const chunkId = typeof payload.chunkId === 'string' ? payload.chunkId : undefined
    const sanitized = sanitizeAnamnesisTelemetryPayload({
      ...getAnamnesisTelemetryEnvironment(),
      ...payload,
    })

    if (consultationId && chunkId) {
      mirrorChunkLifecycleToObservability({
        consultationId,
        event: name,
        chunkId,
        sequence: typeof sanitized.sequence === 'number' ? sanitized.sequence : undefined,
        durationMs: typeof sanitized.durationMs === 'number' ? sanitized.durationMs : undefined,
        provider: typeof sanitized.provider === 'string' ? sanitized.provider : undefined,
      })
    }

    const transactionName = EVENT_TO_SENTRY_TRANSACTION[name]
    if (transactionName) {
      withAnamnesisSentrySpan(
        transactionName,
        {
          consultationId,
          chunkId,
          metadata: sanitized,
        },
        () => undefined,
      )
    }

    if (EXPECTED_OPERATIONAL_EVENTS.has(name)) {
      runSentrySafely(() => {
        Sentry.addBreadcrumb?.({
          category: 'anamnesis.operational',
          message: `anamnesis:${name}`,
          level: 'info',
          data: sanitizeSentryAnamnesisContext({
            ...sanitized,
            expected: true,
          }),
        })
      })
    } else if (STREAMING_WARNING_EVENTS.has(name)) {
      captureAnamnesisSentryMessage(`anamnesis:${name}`, 'warning', {
        consultationId,
        chunkId,
        event: name,
        metadata: sanitized,
      })
    }
  })
}

export const bindAnamnesisObservabilityConsultation = (options: {
  consultationId: string
  doctorId?: string | null
  language?: string
  offlineMode?: boolean
  fallbackMode?: string | null
}): void => {
  void import('@/shared/observability/frontend-observability')
    .then(({ bindAnamnesisLangfuseConsultation }) => bindAnamnesisLangfuseConsultation(options))
    .catch(() => {
      bindAnamnesisSentryConsultation(options)
    })
}

export const unbindAnamnesisObservabilityConsultation = (consultationId?: string): void => {
  void import('@/shared/observability/frontend-observability')
    .then(({ unbindAnamnesisLangfuseConsultation }) => unbindAnamnesisLangfuseConsultation(consultationId))
    .catch(() => {
      unbindAnamnesisSentryConsultation(consultationId)
    })
}

export const reportAnamnesisObservabilityFailure = (
  error: unknown,
  eventName: AnamnesisTechnicalEventName,
  payload: AnamnesisTelemetryPayload = {},
): void => {
  mirrorAnamnesisTechnicalEventToSentry(eventName, payload)
  runSentrySafely(() => {
    const consultationId =
      (typeof payload.consultationId === 'string' && payload.consultationId) || undefined
    const extra = sanitizeSentryAnamnesisContext({
      ...sanitizeAnamnesisTelemetryPayload(payload),
      errorName: error instanceof Error ? error.name : typeof error,
    })
    Sentry.captureException?.(error, {
      tags: {
        feature: 'anamnesis-realtime',
        event: eventName,
        ...(consultationId ? { consultation_id: consultationId } : {}),
      },
      extra,
    })
  })
}
